import { useState } from "react";
import { Handshake, Package } from "lucide-react";
import { brl } from "@/lib/api";
import { EmptyState, Surface } from "@/components/ds";
import { BarsCompare, C } from "./charts";

const Cell = ({ label, value, tone, testid }) => (
  <div className="rounded-lg border border-white/10 bg-secondary/40 px-4 py-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-1 font-num text-lg font-semibold ${tone || ""}`}>{value}</p>
  </div>
);

export function FinancePartners({ data }) {
  const { rows, totals } = data.partners;
  const [sort, setSort] = useState("sold");
  const [openId, setOpenId] = useState(null);

  const sorted = [...rows].sort((a, b) => b[sort] - a[sort]);
  const inPartners = rows
    .filter((r) => r.stock_units > 0 || r.stock_unpriced_units > 0)
    .sort((a, b) => b.stock_value - a.stock_value);

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Handshake}
        title="Sem movimento de parceiros no período"
        description="Reposições, vendas e fechamentos do consignado aparecem aqui."
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-partners">
      <Surface className="p-5" data-testid="partners-stock-out">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
              <Package className="h-3.5 w-3.5 text-brand" /> Estoque fora da empresa
            </p>
            <p
              className="mt-1 font-num text-3xl font-semibold text-brand"
              data-testid="partners-stock-value"
            >
              {brl(totals.stock_value)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Mercadorias atualmente em parceiros ·{" "}
              <span className="font-num">
                {Number(totals.stock_units || 0).toLocaleString("pt-BR")} peça(s) em{" "}
                {totals.stock_partners} parceiro(s)
              </span>
            </p>
            {totals.stock_unpriced_units > 0 && (
              <p className="mt-1 text-xs text-amber-300" data-testid="partners-stock-unpriced">
                {Number(totals.stock_unpriced_units).toLocaleString("pt-BR")} un em{" "}
                {totals.stock_unpriced_items} item(ns) sem preço definido — fora deste valor
              </p>
            )}
          </div>
          <p className="shrink-0 rounded-full border border-white/10 bg-secondary/40 px-3 py-1 text-[11px] text-muted-foreground">
            Estoque atual — independente do período selecionado
          </p>
        </div>
        <p className="mt-3 border-t border-white/10 pt-3 text-xs text-muted-foreground">
          Valor patrimonial: não é faturamento e não entra em Vendido, Recebido ou A receber.
        </p>
      </Surface>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Cell label="Enviado em reposições" value={brl(totals.sent)} testid="partners-sent" />
        <Cell label="Vendido pelos parceiros" value={brl(totals.sold)} tone="text-emerald-300" testid="partners-sold" />
        <Cell label="Fechado no período" value={brl(totals.closed)} testid="partners-closed" />
        <Cell label="Recebido em fechamentos" value={brl(totals.received)} tone="text-emerald-300" testid="partners-received" />
        <Cell label="Pendente" value={brl(totals.pending)} tone="text-amber-300" testid="partners-pending" />
      </div>

      <Surface className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-medium md:text-lg">Ranking de parceiros</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Reposição enviada não é venda: o faturamento acontece quando o parceiro vende.
            </p>
          </div>
          <div className="flex gap-1">
            {[
              ["sold", "Faturamento"],
              ["units_sold", "Quantidade"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSort(key)}
                data-testid={`partner-sort-${key}`}
                className={`rounded-full border px-3 py-1 text-[11px] transition-colors ${
                  sort === key
                    ? "border-brand/50 bg-brand/15 text-brand"
                    : "border-white/10 text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 divide-y divide-white/5">
          {sorted.map((r) => (
            <div key={r.partner_id} data-testid={`partner-finance-${r.partner_id}`}>
              <button
                onClick={() => setOpenId((id) => (id === r.partner_id ? null : r.partner_id))}
                className="flex w-full items-center gap-3 py-3 text-left"
                data-testid={`partner-finance-toggle-${r.partner_id}`}
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.name}</span>
                <span className="shrink-0 font-num text-xs text-muted-foreground">
                  {r.units_sold} un
                </span>
                <span className="shrink-0 font-num text-sm font-semibold text-emerald-300">
                  {brl(r.sold)}
                </span>
              </button>
              {openId === r.partner_id && (
                <div className="grid gap-2 pb-4 sm:grid-cols-2 xl:grid-cols-5">
                  <Cell
                    label="Estoque atual"
                    value={brl(r.stock_value)}
                    tone="text-brand"
                    testid={`pf-stock-${r.partner_id}`}
                  />
                  <Cell label="Enviado" value={brl(r.sent)} testid={`pf-sent-${r.partner_id}`} />
                  <Cell label="Vendido" value={brl(r.sold)} testid={`pf-sold-${r.partner_id}`} />
                  <Cell label="Fechado" value={brl(r.closed)} testid={`pf-closed-${r.partner_id}`} />
                  <Cell
                    label="Recebido / Pendente"
                    value={`${brl(r.received)} / ${brl(r.pending)}`}
                    testid={`pf-received-${r.partner_id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </Surface>

      {inPartners.length > 0 && (
        <Surface className="p-5" data-testid="partners-stock-list">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <Package className="h-4 w-4 text-brand" /> Produtos fora da empresa
          </h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Mercadorias atualmente em estoque com parceiros — saldo atual, independente do período
          </p>
          <div className="divide-y divide-white/5">
            <div className="flex items-center gap-3 pb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
              <span className="min-w-0 flex-1">Parceiro</span>
              <span className="w-20 shrink-0 text-right">Peças</span>
              <span className="w-28 shrink-0 text-right">Em estoque</span>
            </div>
            {inPartners.map((r) => (
              <div
                key={r.partner_id}
                className="flex items-center gap-3 py-2.5 text-sm"
                data-testid={`partner-stock-row-${r.partner_id}`}
              >
                <span className="min-w-0 flex-1 truncate">
                  {r.name}
                  {r.stock_unpriced_units > 0 && (
                    <span className="ml-2 text-[11px] text-amber-300">
                      {Number(r.stock_unpriced_units).toLocaleString("pt-BR")} un sem preço
                    </span>
                  )}
                </span>
                <span className="w-20 shrink-0 text-right font-num text-xs text-muted-foreground">
                  {Number(r.stock_units || 0).toLocaleString("pt-BR")}
                </span>
                <span className="w-28 shrink-0 text-right font-num font-semibold text-brand">
                  {brl(r.stock_value)}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-2.5 text-sm font-semibold">
              <span className="min-w-0 flex-1">Total</span>
              <span className="w-20 shrink-0 text-right font-num text-xs text-muted-foreground">
                {Number(totals.stock_units || 0).toLocaleString("pt-BR")}
              </span>
              <span className="w-28 shrink-0 text-right font-num text-brand">
                {brl(totals.stock_value)}
              </span>
            </div>
          </div>
        </Surface>
      )}

      <Surface className="p-5">
        <h3 className="text-sm font-medium">Parceiros por faturamento</h3>
        <p className="mb-3 text-xs text-muted-foreground">Vendas realizadas no período</p>
        <BarsCompare
          data={sorted.slice(0, 8).map((r) => ({ label: r.name.slice(0, 12), sold: r.sold, received: r.received }))}
          bars={[
            { key: "sold", name: "Vendido", color: C.sold },
            { key: "received", name: "Recebido", color: C.received },
          ]}
          testid="chart-partners"
        />
      </Surface>
    </div>
  );
}
